import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
    const { login } = useCustomerAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const oauthError = params.get("error");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState(oauthError ? "Google sign-in failed. Please try again or use email." : "");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate(next, { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const startGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(next);
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="login-page">
            <Header />
            <main className="max-w-md mx-auto px-6 py-16">
                <span className="label-eyebrow">Welcome back</span>
                <h1 className="mt-3 font-display text-4xl text-inkDark">Sign in</h1>
                <p className="mt-1 text-inkMuted">Continue your boutique journey.</p>

                <button
                    type="button"
                    onClick={startGoogle}
                    className="mt-8 w-full flex items-center justify-center gap-3 rounded-xl border border-inkDark/10 bg-white px-4 py-3 hover:border-skyblueDeep transition-colors"
                    data-testid="login-google-button"
                >
                    <GoogleG />
                    <span className="text-sm text-inkDark font-medium">Continue with Google</span>
                </button>

                <div className="my-6 flex items-center gap-3 text-xs text-inkMuted">
                    <div className="flex-1 h-px bg-inkDark/10" />
                    or with email
                    <div className="flex-1 h-px bg-inkDark/10" />
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                    <label className="block">
                        <span className="label-eyebrow">Email</span>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="login-email-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">Password</span>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="login-password-input"
                        />
                    </label>
                    {err ? <div className="rounded-xl bg-red-50 text-red-600 px-4 py-3 text-sm" data-testid="login-error">{err}</div> : null}
                    <button type="submit" disabled={busy} className="btn-pill w-full justify-center disabled:opacity-50" data-testid="login-submit">
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                </form>

                <div className="mt-6 text-sm text-inkMuted">
                    New to Sukaanyas?{" "}
                    <Link to={`/signup?next=${encodeURIComponent(next)}`} className="text-skyblueDeep hover:underline" data-testid="login-signup-link">
                        Create an account
                    </Link>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function GoogleG() {
    return (
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.5 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.5 29.5 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.3 0 10.2-2 13.8-5.4l-6.4-5.4C29.4 34.7 26.8 36 24 36c-5 0-9.4-3.3-11.1-7.9l-6.6 5.1C9.7 39.6 16.3 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.4 5.4c-.4.4 6.5-4.7 6.5-15.1 0-1.2-.1-2.4-.4-3.5z"/>
        </svg>
    );
}
